import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, Check, Home, ChevronLeft, Volume2, VolumeX, Store, LogOut, UtensilsCrossed, Flame, Drumstick, Pizza, Sandwich, Clock, User } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CartDrawer from "@/components/CartDrawer";
import OrderHistoryDrawer from "@/components/OrderHistoryDrawer";
import KrunchBoxWizard from "@/pages/customer/KrunchBoxWizard";
import PizzaBuilder from "@/pages/customer/PizzaBuilder";
import BurgerCustomizer from "@/pages/customer/BurgerCustomizer";
import MenuSearch, { MenuSearchTrigger, getSectionIdForItem, getConceptForItem } from "@/components/MenuSearch";
import CategoryTabBar from "@/components/CategoryTabBar";
import { 
  CONCEPTS, BISCUITS, CHICKEN_PIECES, 
  WING_SAUCES as DEFAULT_WING_SAUCES, 
  DIPPING_SAUCES as DEFAULT_DIPPING_SAUCES,
  IMAGES, BRAND_COLORS, BRAND_LOGOS, formatPrice, getFreeSauceCount, EXTRA_SAUCE_PRICE
} from "@/lib/menuData";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ title, color, id }) {
  return (
    <div
      id={id}
      style={{
        padding: "14px 16px 8px",
        fontSize: 11,
        fontWeight: 500,
        color: "#6b7280",
        textTransform: "uppercase",
        letterSpacing: ".06em",
      }}
    >
      {title}
    </div>
  );
}

// Concept-tinted illustrated placeholder used when an item has no photo.
// Chicken -> tan + amber drumstick, Pizza -> tan + coral pizza, Lunch -> green + sandwich.
const CONCEPT_PLACEHOLDER_STYLES = {
  chicken: { bg: "#F4E8DC", iconColor: "#C67738", Icon: Drumstick },
  pizza:   { bg: "#E8DDD5", iconColor: "#F0997B", Icon: Pizza },
  lunch:   { bg: "#E3EBDC", iconColor: "#5C8A3D", Icon: Sandwich },
};

// 2-column grid card matching the mockup: name -> meta (Popular pill or spacer)
// -> price -> 96px image block with floating "+" pinned to the bottom-right.
// `concept` prop wins over `item.concept` so the correct concept-tinted placeholder
// renders regardless of how the backend categorises the item.
function MenuCard({ name, price, image, onAdd, item, color, concept }) {
  const isPopular = !!item?.popular;
  const conceptId = (concept || item?.concept || "").toLowerCase();
  const ph = CONCEPT_PLACEHOLDER_STYLES[conceptId] || {
    bg: (color || BRAND_COLORS.sunsetOrange) + "1A",
    iconColor: color || BRAND_COLORS.sunsetOrange,
    Icon: UtensilsCrossed,
  };
  const PH_Icon = ph.Icon;
  const photo = image || item?.image;
  const slug = (name || "").toLowerCase().replace(/\s+/g, "-");

  return (
    <div
      data-testid={`menu-card-${slug}`}
      className="item-card flex flex-col px-3 pt-3 pb-2.5 cursor-pointer active:bg-slate-50 transition-colors h-full"
      onClick={onAdd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAdd?.();
        }
      }}
    >
      <span className="text-[13px] font-medium leading-snug text-foreground line-clamp-2" style={{ color: BRAND_COLORS.darkSlate, fontFamily: "Manrope" }}>
        {name}
      </span>

      {/* Meta row — fixed minHeight so rows stay aligned across grid */}
      <div className="mt-1.5" style={{ minHeight: 18 }}>
        {isPopular && (
          <span
            data-testid="popular-badge"
            className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full"
            style={{ background: "#FFF0E6", color: "#854F0B", fontSize: 10, fontWeight: 600 }}
          >
            <Flame className="h-2.5 w-2.5" /> Popular
          </span>
        )}
      </div>

      <span className="text-[12px] mt-0.5 font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
        {formatPrice(price)}
      </span>

      {/* Image block (always rendered for layout consistency) */}
      <div
        className="item-img-block relative mt-2 rounded-xl overflow-hidden flex-shrink-0"
        style={{ height: "var(--grid-item-img-height, 96px)", background: ph.bg }}
      >
        {photo ? (
          <img
            src={photo}
            alt={name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PH_Icon className="h-9 w-9" style={{ color: ph.iconColor, opacity: 0.85 }} strokeWidth={1.5} />
          </div>
        )}

        <button
          aria-label={`Add ${name}`}
          data-testid={`add-btn-${slug}`}
          onClick={(e) => { e.stopPropagation(); onAdd?.(); }}
          className="add-btn absolute h-7 w-7 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-slate-50 active:scale-95 transition-all"
          style={{ bottom: 6, right: 6, border: "0.5px solid #d1d5db" }}
        >
          <Plus className="h-3.5 w-3.5" style={{ color: BRAND_COLORS.darkSlate }} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// Skeleton placeholder for the 2-column menu grid while data is loading.
function MenuGridSkeleton() {
  return (
    <div className="grid grid-cols-2 border-t border-slate-200">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={`p-3 border-b border-slate-200 ${i % 2 === 0 ? "border-r" : ""}`}
        >
          <div className="h-3 w-3/4 bg-slate-200 rounded animate-pulse mb-2" />
          <div className="h-2.5 w-1/2 bg-slate-200 rounded animate-pulse mb-1" />
          <div className="h-2 w-1/4 bg-slate-200 rounded animate-pulse mb-2" />
          <div className="h-24 w-full bg-slate-200 rounded-xl animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function PieceCounter({ pieces, total, onChange }) {
  const currentTotal = Object.values(pieces).reduce((s, v) => s + v, 0);
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate }}>
        Select {total} pieces ({currentTotal}/{total})
      </p>
      {CHICKEN_PIECES.map((p) => (
        <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white">
          <span className="font-medium text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{p.name}</span>
          <div className="flex items-center gap-2">
            <button
              data-testid={`piece-minus-${p.id}`}
              className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 disabled:opacity-30"
              style={{ backgroundColor: "#E8E8E8" }}
              onClick={() => onChange(p.id, Math.max(0, (pieces[p.id] || 0) - 1))}
              disabled={(pieces[p.id] || 0) === 0}
            >
              <Minus className="h-3.5 w-3.5" style={{ color: BRAND_COLORS.darkSlate }} />
            </button>
            <span className="text-sm font-bold min-w-[20px] text-center">{pieces[p.id] || 0}</span>
            <button
              data-testid={`piece-plus-${p.id}`}
              className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 disabled:opacity-30"
              style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "30" }}
              onClick={() => onChange(p.id, (pieces[p.id] || 0) + 1)}
              disabled={currentTotal >= total}
            >
              <Plus className="h-3.5 w-3.5" style={{ color: BRAND_COLORS.tropicalAqua }} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OrderPage({ initialCartOpen = false }) {
  return <OrderPageInner initialCartOpen={initialCartOpen} />;
}

function OrderPageInner({ initialCartOpen = false }) {
  const { brand } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [concept, setConcept] = useState(brand || "chicken");
  const [cartOpen, setCartOpen] = useState(initialCartOpen);
  const [menuData, setMenuData] = useState({ items: [], sides: [], categories: [] });
  const [menuLoading, setMenuLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState("pieces");
  const [pizzaBuilderOpen, setPizzaBuilderOpen] = useState(false);
  const [pizzaItem, setPizzaItem] = useState(null);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [preSelectedBurger, setPreSelectedBurger] = useState(null);
  const [lunchMealSides, setLunchMealSides] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [conceptStatus, setConceptStatus] = useState({}); // { chicken: {currentStatus}, pizza: {...}, lunch: {...} }
  const [showAccount, setShowAccount] = useState(false);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);

  // In-store mode detection
  const isInStoreMode = searchParams.get("mode") === "instore";
  const [employeeSession, setEmployeeSession] = useState(null);
  const { clearCart } = useCart();
  const { customer, isAuthenticated, getOrderHistory } = useCustomerAuth();

  // Poll active orders every 30s to drive the red dot on the profile icon
  useEffect(() => {
    if (!isAuthenticated) {
      setActiveOrdersCount(0);
      return undefined;
    }
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const history = await getOrderHistory();
        if (cancelled) return;
        const active = (history || []).filter((o) =>
          ["new", "pending", "preparing", "accepted", "ready"].includes(o.status)
        );
        setActiveOrdersCount(active.length);
      } catch { /* ignore */ }
    };
    fetchActive();
    const interval = setInterval(fetchActive, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAuthenticated, getOrderHistory]);

  const handleReorder = (items) => {
    (items || []).forEach((item) => {
      // We can't call addItem here without going through the cart context; simplest is to add each
      // via the same shape used elsewhere in this page. Users will see the items appear.
      // (The Hub page reorder does the same shape.)
      addSimpleItem(item, item.concept, item.category);
    });
    setShowAccount(false);
  };

  // Check employee session on mount
  useEffect(() => {
    if (isInStoreMode) {
      const session = sessionStorage.getItem("employeeSession");
      if (session) {
        const parsed = JSON.parse(session);
        // Check session timeout (30 minutes)
        if (Date.now() - parsed.loginTime > 30 * 60 * 1000) {
          sessionStorage.removeItem("employeeSession");
          navigate("/employee");
          return;
        }
        setEmployeeSession(parsed);
      } else {
        // Not logged in, redirect to employee login
        navigate("/employee");
      }
    }
  }, [isInStoreMode, navigate]);

  const handleEmployeeLogout = () => {
    sessionStorage.removeItem("employeeSession");
    clearCart();
    navigate("/employee");
  };

  // Meal dialog
  const [mealDialog, setMealDialog] = useState(null);
  const [mealPieces, setMealPieces] = useState({});
  const [selectedBiscuit, setSelectedBiscuit] = useState(null);
  const [isMealPieces, setIsMealPieces] = useState(false);

  // Family meal dialog
  const [familyDialog, setFamilyDialog] = useState(null);
  const [familyPieces, setFamilyPieces] = useState({});

  // Side dialog with size selection
  const [sideDialog, setSideDialog] = useState(null);
  const [selectedSideName, setSelectedSideName] = useState(null);
  const [selectedSideSize, setSelectedSideSize] = useState(null);

  // Wings sauce dialog
  const [wingsDialog, setWingsDialog] = useState(null);
  const [selectedWingSauce, setSelectedWingSauce] = useState(null);

  // Chicken item sauce dialog (tiered free sauces) - Now with quantities
  const [sauceDialog, setSauceDialog] = useState(null);
  const [sauceQuantities, setSauceQuantities] = useState({});

  // Dark chicken add-on dialog (hot sauce/honey only - no dipping sauce)
  const [darkChickenDialog, setDarkChickenDialog] = useState(null);
  const [darkChickenAddons, setDarkChickenAddons] = useState({ hotSauce: false, honey: false });

  // Inventory data from API
  const [wingSauces, setWingSauces] = useState(DEFAULT_WING_SAUCES);
  const [dippingSauces, setDippingSauces] = useState(DEFAULT_DIPPING_SAUCES);

  // Video banner state (for KKC)
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const { addItem, itemCount } = useCart();

  // Fetch inventory data
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const [wingRes, dipRes, lunchSidesRes] = await Promise.all([
          axios.get(`${API}/inventory/wing-sauces`),
          axios.get(`${API}/inventory/dipping-sauces`),
          axios.get(`${API}/inventory/lunch-sides`)
        ]);
        if (wingRes.data?.length > 0) setWingSauces(wingRes.data);
        if (dipRes.data?.length > 0) setDippingSauces(dipRes.data);
        if (lunchSidesRes.data?.length > 0) setLunchMealSides(lunchSidesRes.data.filter(s => s.available));
      } catch (e) {
        console.error("Failed to fetch inventory, using defaults");
      }
    };
    fetchInventory();
  }, []);

  // Set concept from URL param
  useEffect(() => {
    if (brand && ["chicken", "pizza", "lunch"].includes(brand)) {
      setConcept(brand);
    }
  }, [brand]);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu`);
      setMenuData(res.data);
    } catch (e) { console.error("Failed to fetch menu", e); }
    finally { setMenuLoading(false); }
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // Poll concept status every 60s so the closed-kitchen gate updates without a refresh
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/concepts/status`);
        setConceptStatus(res.data || {});
      } catch (_) { /* keep previous state */ }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 60_000);
    return () => clearInterval(interval);
  }, []);

  // In-store mode always bypasses the closed-kitchen gate (staff can order for walk-ins)
  const currentConceptStatus = conceptStatus[concept]?.currentStatus || null;
  const conceptIsOpen = isInStoreMode ? true : (currentConceptStatus ? currentConceptStatus.isOpen !== false : true);
  const conceptName = conceptStatus[concept]?.conceptName || concept;

  // Video banner controls (for KKC)
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Auto-play video when chicken concept is selected
  useEffect(() => {
    if (concept === "chicken" && videoRef.current) {
      videoRef.current.muted = true;
      setIsMuted(true);
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(() => {
        setIsVideoPlaying(false);
      });
    }
  }, [concept]);

  // Memoize a per-category lookup so we don't filter the full menu on every
  // render. Items get bucketed into a `{ [category]: Item[] }` map once per
  // menuData change, then `getItemsByCategory` is just an O(1) map read.
  const itemsByCategory = useMemo(() => {
    const map = {};
    for (const it of (menuData.items || [])) {
      const cat = it.category;
      if (!cat) continue;
      if (!map[cat]) map[cat] = [];
      map[cat].push(it);
    }
    return map;
  }, [menuData.items]);

  const getItemsByCategory = useCallback((cat) => itemsByCategory[cat] || [], [itemsByCategory]);
  const availableSides = menuData.sides?.filter((s) => s.available) || [];

  // Helper functions to categorize chicken items
  // Dark chicken: drumstick, thigh, breast pieces (NOT tenders, NOT wings)
  const isDarkChicken = (item) => {
    const darkCategories = ['craving_chicken', 'meal_pieces', 'family_meal'];
    // Check category or if it's piece-based (not tenders)
    if (darkCategories.includes(item?.category)) return true;
    // Check if name suggests pieces vs tenders
    const name = item?.name?.toLowerCase() || '';
    const isPieces = name.includes('piece') || name.includes('pc') || name.includes('dark');
    const isTender = name.includes('tender');
    return isPieces && !isTender;
  };

  // Tenders: all tender items
  const isTenderItem = (item) => {
    const tenderCategories = ['craving_tenders', 'meal_tenders'];
    if (tenderCategories.includes(item?.category)) return true;
    const name = item?.name?.toLowerCase() || '';
    return name.includes('tender');
  };

  // Calculate free sauces - ONLY tenders get free sauce (1 per 3pc)
  const getItemFreeSauces = (item) => {
    // Check if item is a tender
    const isTender = item?.name?.toLowerCase().includes("tender") || 
                     item?.category?.toLowerCase().includes("tender");
    
    if (!isTender) {
      return 0; // No free sauce for non-tender items
    }
    
    // For tenders: 1 free sauce per 3 pieces
    if (item?.free_sauces !== undefined && item.free_sauces !== null) {
      return item.free_sauces;
    }
    
    // Extract piece count from size (e.g., "3pc", "5pc")
    const sizeMatch = item?.size?.match(/(\d+)/);
    if (sizeMatch) {
      const pieces = parseInt(sizeMatch[1], 10);
      return Math.floor(pieces / 3); // 1 free sauce per 3 pieces
    }
    
    return 0;
  };

  const addSimpleItem = (item, conceptId, category) => {
    addItem({
      name: item.name + (item.size ? ` ${item.size}` : ""),
      concept: conceptId, category, price: item.price, quantity: 1, customizations: {},
    });
    toast.success(`${item.name} added to cart`);
  };

  // Open sauce selection for chicken item
  const openSauceDialog = (item, customizations = {}) => {
    const freeSauces = getItemFreeSauces(item);
    setSauceDialog({ item, freeSauces, customizations });
    setSauceQuantities({});
  };

  // Open dark chicken add-on dialog (hot sauce/honey only)
  const openDarkChickenDialog = (item, customizations = {}) => {
    setDarkChickenDialog({ item, customizations });
    setDarkChickenAddons({ hotSauce: false, honey: false });
  };

  // Handle dark chicken add-on completion - goes straight to cart
  const handleDarkChickenAdd = () => {
    if (!darkChickenDialog) return;
    const { item, customizations } = darkChickenDialog;
    
    const addons = [];
    if (darkChickenAddons.hotSauce) addons.push("Hot Sauce");
    if (darkChickenAddons.honey) addons.push("Honey");
    
    const finalCustomizations = { 
      ...customizations,
      addOns: addons.length > 0 ? addons.join(", ") + " (Free)" : "None"
    };

    addItem({
      name: item.name + (item.size ? ` ${item.size}` : ""),
      concept: "chicken", 
      category: item.category || "craving_chicken",
      price: item.price, 
      quantity: 1,
      customizations: finalCustomizations,
    });
    toast.success(`${item.name} added to cart`);
    setDarkChickenDialog(null);
    setDarkChickenAddons({ hotSauce: false, honey: false });
  };

  // Handle chicken item click - routes to correct dialog based on type
  const handleChickenItemClick = (item) => {
    // Wings always go to wing sauce dialog first
    if (item.category === "wings" || item.name?.toLowerCase().includes("wing")) {
      setWingsDialog(item);
      setSelectedWingSauce(null);
      return;
    }
    
    // Check if it's dark chicken (no dipping sauce, just optional hot sauce/honey)
    if (isDarkChicken(item)) {
      openDarkChickenDialog(item);
      return;
    }
    
    // Tenders and other items go to full sauce dialog
    openSauceDialog(item);
  };

  // Get total sauce quantity
  const getTotalSauceQty = () => {
    return Object.values(sauceQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  // Update sauce quantity
  const updateSauceQty = (sauceId, delta) => {
    const current = sauceQuantities[sauceId] || 0;
    const newQty = current + delta;
    if (newQty < 0) return;
    setSauceQuantities(prev => ({ ...prev, [sauceId]: newQty }));
  };

  // Calculate extra sauce cost
  const getExtraSauceCost = () => {
    if (!sauceDialog) return 0;
    const total = getTotalSauceQty();
    const extraCount = Math.max(0, total - sauceDialog.freeSauces);
    return extraCount * EXTRA_SAUCE_PRICE;
  };

  const handleSauceAdd = () => {
    if (!sauceDialog) return;
    const { item, freeSauces, customizations } = sauceDialog;
    const totalSauces = getTotalSauceQty();
    const extraCost = getExtraSauceCost();
    
    let sauceCustomization = "No sauce";
    if (totalSauces > 0) {
      const sauceList = dippingSauces
        .filter((s) => sauceQuantities[s.id] > 0)
        .map((s) => `${sauceQuantities[s.id]}x ${s.name}`)
        .join(", ");
      sauceCustomization = extraCost > 0 
        ? `${sauceList} (+$${extraCost.toFixed(2)})`
        : `${sauceList} (Free)`;
    }

    addItem({
      name: item.name + (item.size ? ` ${item.size}` : ""),
      concept: "chicken", 
      category: item.category || "favorite",
      price: item.price + extraCost, 
      quantity: 1,
      customizations: { ...customizations, sauces: sauceCustomization },
    });
    toast.success(`${item.name} added to cart`);
    setSauceDialog(null);
    setSauceQuantities({});
  };

  // Wings with flavor selection (all wings require flavor selection)
  const handleWingsClick = (item) => {
    setWingsDialog(item);
    setSelectedWingSauce(null);
  };

  const handleWingsAdd = () => {
    if (!selectedWingSauce || !wingsDialog) return;
    // After wing flavor, open dipping sauce dialog (wings get free sauces based on count)
    const item = { ...wingsDialog, customizations: { wingFlavor: selectedWingSauce } };
    setWingsDialog(null);
    setSelectedWingSauce(null);
    openSauceDialog(item, { wingFlavor: selectedWingSauce });
  };

  // Side prices by size - now uses database prices if available
  const getSidePrice = (side, size) => {
    // If side has prices from database, use them
    if (side?.prices && side.prices[size] !== undefined) {
      return side.prices[size];
    }
    // Fallback to defaults
    if (side?.name === "Wedges" || side === "Wedges") {
      if (size === "Regular") return 2.99;
      if (size === "Large") return 4.99;
      if (size === "Family") return 5.99;
    }
    return size === "Regular" ? 2.99 : 5.99;
  };

  // Side dialog handlers
  const handleSideClick = (side) => {
    setSideDialog(side);
    setSelectedSideName(side.name);
    setSelectedSideSize(null);
  };

  const handleSideAdd = () => {
    if (!selectedSideName || !selectedSideSize || !sideDialog) return;
    const price = getSidePrice(sideDialog, selectedSideSize);
    addItem({
      name: `${selectedSideName} (${selectedSideSize})`,
      concept: "chicken", category: "side",
      price: price, quantity: 1,
      customizations: { size: selectedSideSize },
    });
    toast.success(`${selectedSideName} (${selectedSideSize}) added to cart`);
    setSideDialog(null);
    setSelectedSideName(null);
    setSelectedSideSize(null);
  };

  const getMealPieceCount = () => {
    if (!mealDialog) return 0;
    const match = mealDialog.size?.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getFamilyPieceCount = () => {
    if (!familyDialog) return 0;
    const match = familyDialog.size?.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const handleMealAdd = () => {
    if (!selectedBiscuit || !mealDialog) return;
    const customizations = { biscuit: selectedBiscuit };
    if (isMealPieces) {
      const pieceNames = CHICKEN_PIECES.filter((p) => mealPieces[p.id] > 0)
        .map((p) => `${mealPieces[p.id]}x ${p.name}`).join(", ");
      customizations.pieces = pieceNames;
    }
    
    // Determine if this is dark chicken (pieces) or tenders
    const isTender = mealDialog.category === "meal_tenders" || 
                     mealDialog.name?.toLowerCase().includes("tender");
    
    setMealDialog(null); 
    setSelectedBiscuit(null); 
    setMealPieces({}); 
    setIsMealPieces(false);
    
    if (isTender) {
      // Tenders go to full dipping sauce dialog
      const item = { ...mealDialog, category: "meal_tenders" };
      openSauceDialog(item, customizations);
    } else {
      // Dark chicken goes to hot sauce/honey dialog
      const item = { ...mealDialog, category: "meal_pieces" };
      openDarkChickenDialog(item, customizations);
    }
  };

  const handleFamilyAdd = () => {
    if (!familyDialog) return;
    const pieceNames = CHICKEN_PIECES.filter((p) => familyPieces[p.id] > 0)
      .map((p) => `${familyPieces[p.id]}x ${p.name}`).join(", ");
    
    // Family meals are dark chicken - go to hot sauce/honey dialog
    const item = { ...familyDialog, category: "family_meal" };
    setFamilyDialog(null); 
    setFamilyPieces({});
    openDarkChickenDialog(item, { pieces: pieceNames });
  };

  const openPizzaBuilder = (item) => { setPizzaItem(item); setPizzaBuilderOpen(true); };
  const openBurger = (burger) => { setPreSelectedBurger(burger); setBurgerOpen(true); };

  // Handle a search result selection: switch tab, scroll to the right section,
  // and briefly highlight the section header.
  const handleSearchSelectItem = useCallback((item) => {
    const targetConcept = getConceptForItem(item);
    const sectionId = getSectionIdForItem(item);
    if (targetConcept !== concept) {
      setConcept(targetConcept);
      navigate(`/order/${targetConcept}${isInStoreMode ? "?mode=instore" : ""}`);
    }
    // Wait for tab content to render before scrolling
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("search-highlight");
        setTimeout(() => el.classList.remove("search-highlight"), 2200);
      }
    }, targetConcept !== concept ? 250 : 50);
  }, [concept, navigate, isInStoreMode]);

  // Keyboard shortcut: "/" to open search (ignored when typing in inputs)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      const editable = e.target?.isContentEditable;
      if (tag === "input" || tag === "textarea" || editable) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const currentConcept = CONCEPTS.find((c) => c.id === concept);
  
  // Chicken categories
  const wingsItems = getItemsByCategory("wings");
  const krunchPieces = getItemsByCategory("krunch_box_pieces");
  const krunchTenders = getItemsByCategory("krunch_box_tenders");
  const mealPiecesItems = getItemsByCategory("meal_pieces");
  const mealTendersItems = getItemsByCategory("meal_tenders");
  const sandwiches = getItemsByCategory("sandwich");
  const seafoodItems = getItemsByCategory("seafood");
  const familyMeals = getItemsByCategory("family_meal");
  const cravingChicken = getItemsByCategory("craving_chicken");
  const cravingTenders = getItemsByCategory("craving_tenders");
  const biscuitItems = getItemsByCategory("biscuit");
  
  // Pizza categories
  const specialtyPizza = getItemsByCategory("specialty_pizza");
  const specialtyPizzas = getItemsByCategory("specialty_pizza");
  const regularPizzas = getItemsByCategory("regular_pizza");
  
  // Unified Burgers: combine lunch_burger + hamburger + cheeseburger so all admin-managed
  // burgers (regardless of category) show up under the Lunch tab's "Island Burgers" section.
  const allBurgerItems = [
    ...getItemsByCategory("lunch_burger"),
    ...getItemsByCategory("hamburger"),
    ...getItemsByCategory("cheeseburger"),
  ];

  const mealPieceTotal = Object.values(mealPieces).reduce((s, v) => s + v, 0);
  const familyPieceTotal = Object.values(familyPieces).reduce((s, v) => s + v, 0);

  // Sticky category bar entries — one per visible section, in render order.
  // Filtered to only include sections that have at least one item visible.
  const categoryTabs = (() => {
    if (concept === "chicken") {
      return [
        { id: "wings", label: "Wings", sectionId: "section-wings", visible: wingsItems.length > 0 },
        { id: "krunch_box_pieces", label: "Krunch Boxes", sectionId: "section-krunch_box_pieces", visible: krunchPieces.length > 0 },
        { id: "krunch_box_tenders", label: "Tender Boxes", sectionId: "section-krunch_box_tenders", visible: krunchTenders.length > 0 },
        { id: "meal_pieces", label: "Chicken + Biscuit", sectionId: "section-meal_pieces", visible: mealPiecesItems.length > 0 },
        { id: "meal_tenders", label: "Tenders + Biscuit", sectionId: "section-meal_tenders", visible: mealTendersItems.length > 0 },
        { id: "sandwich", label: "Sandwiches", sectionId: "section-sandwich", visible: sandwiches.length > 0 },
        { id: "seafood", label: "Shrimp", sectionId: "section-seafood", visible: seafoodItems.length > 0 },
        { id: "family_meal", label: "Family Meals", sectionId: "section-family_meal", visible: familyMeals.length > 0 },
        { id: "craving_chicken", label: "Craving More", sectionId: "section-craving_chicken", visible: cravingChicken.length > 0 },
        { id: "craving_tenders", label: "Craving Tenders", sectionId: "section-craving_tenders", visible: cravingTenders.length > 0 },
        { id: "biscuit", label: "Biscuits", sectionId: "section-biscuit", visible: biscuitItems.length > 0 },
        { id: "sides", label: "Sides", sectionId: "section-sides", visible: true },
      ].filter((c) => c.visible);
    }
    if (concept === "pizza") {
      return [
        { id: "specialty_pizza", label: "Specialty", sectionId: "section-specialty_pizza", visible: specialtyPizzas.length > 0 },
        { id: "regular_pizza", label: "Classic", sectionId: "section-regular_pizza", visible: regularPizzas.length > 0 },
        { id: "build_your_own", label: "Build Your Own", sectionId: "section-build_your_own", visible: true },
      ].filter((c) => c.visible);
    }
    if (concept === "lunch") {
      return [
        { id: "burgers", label: "Burgers", sectionId: "section-burgers", visible: allBurgerItems.length > 0 },
        { id: "lunch_special", label: "Daily Specials", sectionId: "section-lunch_special", visible: getItemsByCategory("lunch_special").length > 0 },
        { id: "lunch_sandwich", label: "Sandwiches", sectionId: "section-lunch_sandwich", visible: getItemsByCategory("lunch_sandwich").length > 0 },
        { id: "lunch_chicken", label: "Chicken", sectionId: "section-lunch_chicken", visible: getItemsByCategory("lunch_chicken").length > 0 },
        { id: "lunch_seafood", label: "Seafood", sectionId: "section-lunch_seafood", visible: getItemsByCategory("lunch_seafood").length > 0 },
        { id: "lunch_side", label: "Sides & Snacks", sectionId: "section-lunch_side", visible: getItemsByCategory("lunch_side").length > 0 },
        { id: "lunch_ready", label: "Ready-To-Go", sectionId: "section-lunch_ready", visible: getItemsByCategory("lunch_ready").length > 0 },
      ].filter((c) => c.visible);
    }
    return [];
  })();

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
      {/* In-Store Mode Banner */}
      {isInStoreMode && employeeSession && (
        <div 
          className="h-12 flex items-center justify-between px-4"
          style={{ backgroundColor: "#0066cc" }}
          data-testid="instore-order-banner"
        >
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-white" />
            <span className="text-white font-semibold text-sm">IN-STORE MODE</span>
            <span className="text-white/70 text-sm">|</span>
            <span className="text-white/90 text-sm">{employeeSession.name}</span>
          </div>
          <button
            data-testid="order-employee-logout-btn"
            className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium"
            onClick={handleEmployeeLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}

      {/* Header */}
      <header className={`sticky ${isInStoreMode ? 'top-12' : 'top-0'} z-40 backdrop-blur-md border-b px-4 py-3`} style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Back to Hub Button */}
            <Button
              data-testid="back-to-hub-btn"
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-white hover:bg-white/10"
              onClick={() => navigate(isInStoreMode ? "/hub?mode=instore" : "/hub")}
            >
              <ChevronLeft className="h-5 w-5" />
              <Home className="h-4 w-4" />
            </Button>
            <div className="h-6 w-px bg-white/30" />
            <img src={BRAND_LOGOS.main} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
            <Badge 
              className="text-[10px] font-medium px-2 py-0" 
              style={{ backgroundColor: isInStoreMode ? "#0066cc" : BRAND_COLORS.tropicalAqua, color: "white" }} 
              data-testid="pickup-badge"
            >
              {isInStoreMode ? "In-Store Order" : "Pick-up / To-Go"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <MenuSearchTrigger onOpen={() => setSearchOpen(true)} />
            {isAuthenticated && customer && (
              <Button
                data-testid="order-page-account-btn"
                variant="outline"
                size="sm"
                className="relative rounded-full h-10 w-10 p-0 border-2"
                style={{ borderColor: BRAND_COLORS.tropicalAqua, backgroundColor: "transparent" }}
                onClick={() => setShowAccount(true)}
                aria-label="My Account"
              >
                {customer.picture ? (
                  <img src={customer.picture} alt="" className="h-8 w-8 rounded-full" />
                ) : (
                  <User className="h-5 w-5 text-white" />
                )}
                {activeOrdersCount > 0 && (
                  <span
                    data-testid="active-order-dot"
                    className="absolute -top-0.5 -right-0.5 rounded-full h-4 min-w-4 px-1 text-[9px] font-bold text-white flex items-center justify-center border-2"
                    style={{
                      backgroundColor: "#EF4444",
                      borderColor: BRAND_COLORS.deepTeal,
                      animation: "pulse 2s ease-in-out infinite",
                    }}
                    title={`${activeOrdersCount} order${activeOrdersCount === 1 ? "" : "s"} in progress`}
                  >
                    {activeOrdersCount}
                  </span>
                )}
              </Button>
            )}
            <Button 
              data-testid="cart-button" 
              variant="outline" 
              size="sm" 
              className="relative rounded-full h-10 w-10 p-0 border-2"
              style={{ borderColor: BRAND_COLORS.tropicalAqua, backgroundColor: "transparent" }}
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart className="h-5 w-5 text-white" />
              {itemCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center"
                  style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
                >
                  {itemCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Concept Tabs — flat underline style per mockup */}
      <div className="sticky top-[65px] z-30 flex border-b border-slate-200 bg-white" data-testid="concept-tabs">
        <div className="flex w-full max-w-3xl mx-auto">
          {CONCEPTS.map((c) => {
            const active = concept === c.id;
            const cStatus = conceptStatus[c.id]?.currentStatus;
            const cOpen = isInStoreMode ? true : (cStatus ? cStatus.isOpen !== false : true);
            return (
              <button
                key={c.id}
                data-testid={`tab-${c.id}`}
                onClick={() => { setConcept(c.id); navigate(`/order/${c.id}${isInStoreMode ? "?mode=instore" : ""}`); }}
                className="concept-tab flex-1 py-3 text-[13px] font-medium text-center transition-all relative"
                style={{
                  color: active ? BRAND_COLORS.darkSlate : (cOpen ? "#6b7280" : "#9CA3AF"),
                  borderBottom: active ? `2px solid ${BRAND_COLORS.darkSlate}` : "2px solid transparent",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                {c.shortName}
                {!cOpen && (
                  <span
                    data-testid={`tab-${c.id}-closed`}
                    className="ml-1.5 inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded-full align-middle"
                    style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}
                  >
                    CLOSED
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 pb-24">
        {/* Kitchen-Closed Gate — Phase 15.2. Hides menu + hero banner when the concept is closed. */}
        {!conceptIsOpen && !menuLoading && (
          <div
            data-testid="concept-closed-banner"
            className="mt-6 rounded-2xl p-8 text-center shadow-sm"
            style={{ backgroundColor: "#FEF2F2", border: "2px solid #FECACA" }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: "#FEE2E2" }}>
              <Clock className="h-8 w-8" style={{ color: "#B91C1C" }} />
            </div>
            <h2 className="text-xl font-extrabold mb-2" style={{ fontFamily: "Montserrat", color: "#7F1D1D" }}>
              Sorry! Our kitchen is currently closed
            </h2>
            <p className="text-sm mb-3" style={{ color: "#7F1D1D" }}>
              {currentConceptStatus?.message || `${conceptName} is not accepting orders right now.`}
            </p>
            {currentConceptStatus?.nextOpen && (
              <p
                data-testid="concept-next-open"
                className="text-sm font-semibold inline-block px-3 py-1.5 rounded-full"
                style={{ backgroundColor: "white", color: "#B91C1C", border: "1px solid #FECACA" }}
              >
                🕒 Opens {currentConceptStatus.nextOpen}
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {CONCEPTS.map((c) => {
                if (c.id === concept) return null;
                const otherStatus = conceptStatus[c.id]?.currentStatus;
                const otherOpen = otherStatus ? otherStatus.isOpen !== false : true;
                if (!otherOpen) return null;
                return (
                  <button
                    key={c.id}
                    data-testid={`suggest-${c.id}`}
                    onClick={() => { setConcept(c.id); navigate(`/order/${c.id}${isInStoreMode ? "?mode=instore" : ""}`); }}
                    className="text-xs font-semibold px-3 py-2 rounded-full text-white"
                    style={{ backgroundColor: BRAND_COLORS.deepTeal }}
                  >
                    Try {c.shortName || c.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sticky Category Tab Bar (UberEats-style) sits below the concept tabs.
            Placed here so its sticky context spans the full content height. */}
        {conceptIsOpen && categoryTabs.length > 1 && (
          <CategoryTabBar key={concept} categories={categoryTabs} stickyTop={108} className="-mx-4" />
        )}

        {/* Skeleton loader during initial menu fetch */}
        {menuLoading && menuData.items.length === 0 && (
          <div className="-mx-4 mt-4">
            <MenuGridSkeleton />
          </div>
        )}

        {/* Hero Banner - Concept Specific */}
        {conceptIsOpen && concept === "chicken" && (
          <div className="mt-4 rounded-2xl overflow-hidden h-44 sm:h-52 relative shadow-lg" data-testid="kkc-hero-banner">
            {/* KKC Video Banner */}
            <video
              ref={videoRef}
              src={IMAGES.kkc_video_banner}
              className="w-full h-full object-cover"
              loop
              muted
              playsInline
              autoPlay
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            {/* Mute/Unmute Button */}
            <button
              data-testid="banner-mute-btn"
              onClick={toggleMute}
              className="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition-all hover:scale-110"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5 text-white" />
              ) : (
                <Volume2 className="h-5 w-5 text-white" />
              )}
            </button>
            {/* Brand Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
              <div>
                <img 
                  src={BRAND_LOGOS.chicken} 
                  alt="Krispy Krunchy Chicken" 
                  className="h-10 sm:h-12 mb-1 drop-shadow-lg"
                />
                <p className="text-white/90 text-xs sm:text-sm font-medium" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                  Fresh • Hot • Made to Order
                </p>
              </div>
            </div>
          </div>
        )}

        {conceptIsOpen && concept === "pizza" && (
          <div className="mt-4 rounded-2xl overflow-hidden h-44 sm:h-52 relative shadow-lg" data-testid="pizza-hero-banner">
            {/* Hunt Brothers Pizza Static Banner */}
            <img 
              src={IMAGES.hunt_brothers_banner} 
              alt="Hunt Brothers Pizza" 
              className="w-full h-full object-cover"
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            {/* Brand Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white/90 text-sm sm:text-base font-bold" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)", fontFamily: "Montserrat" }}>
                America&apos;s #1 Convenience Store Pizza
              </p>
            </div>
          </div>
        )}

        {/* Chicken Menu */}
        {conceptIsOpen && concept === "chicken" && (
          <div className="animate-fade-in">
            {/* Free Sauce Info Banner */}
            <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "20" }}>
              <p className="text-sm text-center" style={{ color: BRAND_COLORS.deepTeal }}>
                <strong>Dipping Sauces:</strong> Tenders get 1 free per 3pc • Others +$0.79 each
              </p>
            </div>

            {/* Traditional Wings */}
            {wingsItems.length > 0 && (<>
              <SectionHeader id="section-wings" title="Traditional Wings" color={BRAND_COLORS.sunsetOrange} />
              <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Choose your flavor: Krispy or Buffalo</p>
              <div className="menu-grid">
                {wingsItems.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} Wings`} subtitle="Select wing flavor" price={item.price} image={IMAGES.chicken_hero} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => handleWingsClick(item)} />
                ))}
              </div>
            </>)}

            {/* Krunch Boxes - Chicken */}
            <SectionHeader id="section-krunch_box_pieces" title="Krunch Boxes - Chicken" color={BRAND_COLORS.sunsetOrange} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Includes Regular Side + Biscuit • Select your pieces</p>
            <div className="menu-grid">
              {krunchPieces.map((item) => (
                <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle="Select pieces" price={item.price} image={IMAGES.chicken_hero} color={BRAND_COLORS.sunsetOrange}
                  onAdd={() => { setWizardType("pieces"); setWizardOpen(true); }} />
              ))}
            </div>

            {/* Krunch Boxes - Tenders */}
            <SectionHeader id="section-krunch_box_tenders" title="Krunch Boxes - Tenders" color={BRAND_COLORS.sunsetOrange} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Includes Regular Side + Biscuit • 1 Free Sauce per 3pc</p>
            <div className="menu-grid">
              {krunchTenders.map((item) => {
                const sizeMatch = item.size?.match(/(\d+)/);
                const pieces = sizeMatch ? parseInt(sizeMatch[1], 10) : 0;
                const freeSauces = Math.floor(pieces / 3);
                return (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle={freeSauces > 0 ? `${freeSauces} Free Sauce${freeSauces > 1 ? 's' : ''}` : "Extra sauce +$0.79"} price={item.price} image={IMAGES.tenders} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => { setWizardType("tenders"); setWizardOpen(true); }} />
                );
              })}
            </div>

            {/* Chicken + Biscuit */}
            <SectionHeader id="section-meal_pieces" title="Chicken + Biscuit" color={BRAND_COLORS.sunsetOrange} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Hand-breaded chicken with your choice of biscuit</p>
            <div className="menu-grid">
              {mealPiecesItems.map((item) => (
                <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle="Select pieces" price={item.price} color={BRAND_COLORS.sunsetOrange}
                  onAdd={() => { setMealDialog(item); setSelectedBiscuit(null); setMealPieces({}); setIsMealPieces(true); }} />
              ))}
            </div>

            {/* Tenders + Biscuit */}
            <SectionHeader id="section-meal_tenders" title="Tenders + Biscuit" color={BRAND_COLORS.sunsetOrange} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>1 Free Sauce per 3pc Tenders</p>
            <div className="menu-grid">
              {mealTendersItems.map((item) => {
                const sizeMatch = item.size?.match(/(\d+)/);
                const pieces = sizeMatch ? parseInt(sizeMatch[1], 10) : 0;
                const freeSauces = Math.floor(pieces / 3);
                return (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle={freeSauces > 0 ? `${freeSauces} Free Sauce${freeSauces > 1 ? 's' : ''}` : "Extra sauce +$0.79"} price={item.price} image={IMAGES.tenders} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => { setMealDialog(item); setSelectedBiscuit(null); setMealPieces({}); setIsMealPieces(false); }} />
                );
              })}
            </div>

            {/* Sandwiches (No free sauce) */}
            {sandwiches.length > 0 && (<>
              <SectionHeader id="section-sandwich" title="Sandwiches" color={BRAND_COLORS.sunsetOrange} />
              <div className="menu-grid">
                {sandwiches.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={item.name} subtitle="Extra sauce +$0.79" price={item.price} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => addSimpleItem(item, "chicken", "sandwich")} />
                ))}
              </div>
            </>)}

            {/* Honey Butter Shrimp */}
            {seafoodItems.length > 0 && (<>
              <SectionHeader id="section-seafood" title="Honey Butter Shrimp" color={BRAND_COLORS.sunsetOrange} />
              <div className="menu-grid">
                {seafoodItems.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle={`${item.free_sauces || 1} Free Sauce`} price={item.price} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => openSauceDialog(item)} />
                ))}
              </div>
            </>)}

            {/* Family Meals */}
            {familyMeals.length > 0 && (<>
              <SectionHeader id="section-family_meal" title="Family Meals" color={BRAND_COLORS.sunsetOrange} />
              <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Best value for sharing • Select your pieces</p>
              <div className="menu-grid">
                {familyMeals.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={item.name} subtitle={item.description || `${item.free_sauces || 3} Free Sauces`} price={item.price} image={item.name?.toLowerCase().includes("tender") ? IMAGES.tenders : IMAGES.chicken_hero} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => item.requires_piece_selection ? (() => { setFamilyDialog(item); setFamilyPieces({}); })() : openSauceDialog(item)} />
                ))}
              </div>
            </>)}

            {/* Craving More - Chicken Only */}
            {cravingChicken.length > 0 && (<>
              <SectionHeader id="section-craving_chicken" title="Craving More - Chicken" color={BRAND_COLORS.sunsetOrange} />
              <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Chicken only • Select your pieces • Free Hot Sauce & Honey</p>
              <div className="menu-grid">
                {cravingChicken.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle="Free Hot Sauce & Honey" price={item.price} image={IMAGES.chicken_hero} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => { setFamilyDialog(item); setFamilyPieces({}); }} />
                ))}
              </div>
            </>)}

            {/* Craving More - Tenders Only */}
            {cravingTenders.length > 0 && (<>
              <SectionHeader id="section-craving_tenders" title="Craving More - Tenders" color={BRAND_COLORS.sunsetOrange} />
              <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Tenders only • 1 Free Sauce per 3 pieces</p>
              <div className="menu-grid">
                {cravingTenders.map((item) => {
                  const sizeMatch = item.size?.match(/(\d+)/);
                  const pieces = sizeMatch ? parseInt(sizeMatch[1], 10) : 0;
                  const freeSauces = Math.floor(pieces / 3);
                  return (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} subtitle={`${freeSauces} Free Sauce${freeSauces !== 1 ? 's' : ''}`} price={item.price} image={IMAGES.tenders} color={BRAND_COLORS.sunsetOrange}
                      onAdd={() => openSauceDialog(item)} />
                  );
                })}
              </div>
            </>)}

            {/* Honey Biscuits */}
            {biscuitItems.length > 0 && (<>
              <SectionHeader id="section-biscuit" title="Honey Biscuits" color={BRAND_COLORS.sunsetOrange} />
              <div className="menu-grid">
                {biscuitItems.map((item) => (
                  <MenuCard item={item} concept={concept} key={item.id} name={`${item.size} ${item.name}`} price={item.price} color={BRAND_COLORS.sunsetOrange}
                    onAdd={() => addSimpleItem(item, "chicken", "biscuit")} />
                ))}
              </div>
            </>)}

            {/* Consolidated Sides Section */}
            <SectionHeader id="section-sides" title="Sides" color={BRAND_COLORS.sunsetOrange} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Tap to select size</p>
            <div className="menu-grid">
              {availableSides.map((side) => (
                <div
                  key={side.id}
                  data-testid={`side-card-${side.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 cursor-pointer hover:shadow-md transition-all active:scale-[0.98]"
                  onClick={() => handleSideClick(side)}
                >
                  <p className="font-bold text-sm" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>{side.name}</p>
                  <p className="text-xs mt-1" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                    {side.sizes?.join(" • ") || "Regular • Large"}
                  </p>
                  <p className="text-sm font-bold mt-2" style={{ color: BRAND_COLORS.sunsetOrange }}>From ${side.prices?.Regular || "2.99"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pizza Menu */}
        {conceptIsOpen && concept === "pizza" && (
          <div className="animate-fade-in">
            {/* Pizza Pricing Info Banner */}
            <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <div className="text-center">
                  <p className="text-white/70 text-[10px] uppercase tracking-widest mb-0.5">1st Pizza</p>
                  <p className="text-white font-extrabold text-2xl leading-none" style={{ fontFamily: "Montserrat" }}>
                    $11.99
                  </p>
                </div>
                <div className="text-white/30 text-3xl font-thin">+</div>
                <div className="text-center">
                  <p className="text-white/70 text-[10px] uppercase tracking-widest mb-0.5">Every 2nd Pizza</p>
                  <p className="font-extrabold text-2xl leading-none" style={{ fontFamily: "Montserrat", color: "#59C3C3" }}>
                    $10.99
                  </p>
                </div>
              </div>
              <p className="text-white/50 text-[10px] text-center mt-2">
                Classic & Build-Your-Own pizzas only • Specialty pizzas priced separately
              </p>
            </div>

            {/* Specialty Pizzas */}
            {specialtyPizza.length > 0 && (<>
              <SectionHeader id="section-specialty_pizza_limited" title="🔥 Specialty Pizzas" color={BRAND_COLORS.sunsetOrange} />
              <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Premium toppings at $12.99 each!</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {specialtyPizza.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`limited-pizza-${item.id}`}
                    className="bg-white rounded-2xl shadow-md border-2 overflow-hidden cursor-pointer hover:shadow-xl transition-all active:scale-[0.98]"
                    style={{ borderColor: BRAND_COLORS.sunsetOrange }}
                    onClick={() => openPizzaBuilder({ ...item, isSpecialty: true, isLimitedEdition: true })}
                  >
                    {item.image && (
                      <div className="relative">
                        <img src={item.image} alt={item.name} className="w-full h-48 object-cover" />
                        <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}>
                          LIMITED TIME
                        </div>
                      </div>
                    )}
                    <div className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-bold text-base" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>{item.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Regular Crust Only</p>
                        <span className="font-extrabold text-lg mt-1 block" style={{ color: BRAND_COLORS.sunsetOrange }}>${item.price.toFixed(2)}</span>
                      </div>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}>
                        <Plus className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>)}

            <SectionHeader id="section-specialty_pizza" title="Specialty Pizzas" color={BRAND_COLORS.deepTeal} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Regular Crust Only</p>
            <div className="menu-grid">
              {specialtyPizzas.map((item) => (
                <MenuCard item={item} concept={concept} key={item.id} name={item.name} subtitle="Regular Crust Only" price={item.price} image={item.image || IMAGES.pizza_hero} color={BRAND_COLORS.deepTeal}
                  onAdd={() => openPizzaBuilder({ ...item, isSpecialty: true })} />
              ))}
            </div>

            <SectionHeader id="section-regular_pizza" title="Classic Pizzas" color={BRAND_COLORS.deepTeal} />
            <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Regular or Thin Crust</p>
            <div className="menu-grid">
              {regularPizzas.map((item) => (
                <MenuCard item={item} concept={concept} key={item.id} name={item.name} price={item.price} image={item.image || IMAGES.pizza_hero} color={BRAND_COLORS.deepTeal}
                  onAdd={() => openPizzaBuilder({ ...item, isSpecialty: false })} />
              ))}
            </div>

            <SectionHeader id="section-build_your_own" title="Build Your Own" color={BRAND_COLORS.deepTeal} />
            <div 
              data-testid="build-your-own-pizza" 
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow active:scale-[0.98]"
              onClick={() => openPizzaBuilder({ id: "pz_custom", name: "Build Your Own Pizza", price: 11.99, isSpecialty: false })}
            >
              <div>
                <p className="font-bold text-sm" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>Build Your Own Pizza</p>
                <p className="text-xs mt-0.5" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Choose crust, toppings & Half/Half</p>
                <span className="font-extrabold text-base mt-1 block" style={{ color: BRAND_COLORS.deepTeal }}>$11.99</span>
              </div>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
                <Plus className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        )}

        {/* Lunch / Grill Menu */}
        {conceptIsOpen && concept === "lunch" && (
          <div className="animate-fade-in">
            {/* Hero Banner */}
            <div className="rounded-2xl p-6 mb-6 text-white" style={{ backgroundColor: "#2E7D32" }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <UtensilsCrossed className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Montserrat" }}>Island Grill & Deli</h2>
                  <p className="text-sm opacity-80">Kitchen: 5:30 AM – 8:00 PM</p>
                </div>
              </div>
            </div>

            {/* Island Burgers Section - includes lunch_burger + hamburger + cheeseburger */}
            {allBurgerItems.length > 0 && (
              <>
                <SectionHeader id="section-burgers" title="Island Burgers" color="#2E7D32" />
                <p className="text-xs mb-3" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Tap to customize • Combo & Large Combo available</p>
                <div className="menu-grid mb-6">
                  {allBurgerItems.map((item) => (
                    <div key={item.id} className="relative">
                      <MenuCard 
                        item={item}
                        concept={concept}
                        name={`${item.name}${item.size ? ` (${item.size})` : ""}`} 
                        price={item.price} 
                        color="#2E7D32"
                        onAdd={() => {
                          setPreSelectedBurger(item);
                          setBurgerOpen(true);
                        }}
                        subtitle={item.description || null}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Daily Specials */}
            {getItemsByCategory("lunch_special").length > 0 && (
              <>
                <SectionHeader id="section-lunch_special" title="Daily Specials" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_special").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.name}${item.size ? ` (${item.size})` : ""}`} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_special")} subtitle={item.description || null} />
                  ))}
                </div>
              </>
            )}

            {/* Sandwiches */}
            {getItemsByCategory("lunch_sandwich").length > 0 && (
              <>
                <SectionHeader id="section-lunch_sandwich" title="Sandwiches" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_sandwich").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={item.name} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_sandwich")} />
                  ))}
                </div>
              </>
            )}

            {/* Chicken */}
            {getItemsByCategory("lunch_chicken").length > 0 && (
              <>
                <SectionHeader id="section-lunch_chicken" title="Chicken" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_chicken").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.name}${item.size ? ` (${item.size})` : ""}`} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_chicken")} subtitle={item.description || null} />
                  ))}
                </div>
              </>
            )}

            {/* Seafood */}
            {getItemsByCategory("lunch_seafood").length > 0 && (
              <>
                <SectionHeader id="section-lunch_seafood" title="Seafood" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_seafood").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.name}${item.size ? ` (${item.size})` : ""}`} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_seafood")} />
                  ))}
                </div>
              </>
            )}

            {/* Sides & Snacks */}
            {getItemsByCategory("lunch_side").length > 0 && (
              <>
                <SectionHeader id="section-lunch_side" title="Sides & Snacks" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_side").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.name}${item.size ? ` (${item.size})` : ""}`} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_side")} />
                  ))}
                </div>
              </>
            )}

            {/* Ready-To-Go */}
            {getItemsByCategory("lunch_ready").length > 0 && (
              <>
                <SectionHeader id="section-lunch_ready" title="Ready-To-Go" color="#2E7D32" />
                <div className="menu-grid mb-6">
                  {getItemsByCategory("lunch_ready").map((item) => (
                    <MenuCard item={item} concept={concept} key={item.id} name={`${item.name}${item.size ? ` (${item.size})` : ""}`} price={item.price} color="#2E7D32"
                      onAdd={() => addSimpleItem(item, "lunch", "lunch_ready")} subtitle={item.description || null} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Extra Sides Menu (Coming Soon) */}
        {concept === "extras" && (
          <div className="animate-fade-in">
            <div className="mt-8 text-center py-16 px-6">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "20" }}>
                <ShoppingCart className="h-10 w-10" style={{ color: BRAND_COLORS.tropicalAqua }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                Extra Sides
              </h2>
              <p className="text-base" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Coming Soon!
              </p>
              <p className="text-sm mt-2" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                Check back later for snacks and more
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      {itemCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-bottom bg-gradient-to-t from-white via-white to-transparent">
          <div className="max-w-3xl mx-auto">
            <Button 
              data-testid="floating-cart-btn" 
              className="w-full h-14 rounded-2xl text-white font-bold text-base shadow-lg" 
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} 
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart className="h-5 w-5 mr-2" /> View Cart ({itemCount})
            </Button>
          </div>
        </div>
      )}

      {/* Menu Search Overlay */}
      <MenuSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        menuItems={menuData.items}
        onSelectItem={handleSearchSelectItem}
      />

      {/* Dialogs */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} isInStoreMode={isInStoreMode} />
      <OrderHistoryDrawer open={showAccount} onClose={() => setShowAccount(false)} onReorder={handleReorder} />
      <KrunchBoxWizard open={wizardOpen} onClose={() => setWizardOpen(false)} availableSides={availableSides} menuItems={menuData.items} type={wizardType} />
      <PizzaBuilder open={pizzaBuilderOpen} onClose={() => setPizzaBuilderOpen(false)} pizzaItem={pizzaItem} menuItems={menuData.items} />
      <BurgerCustomizer 
        open={burgerOpen} 
        onClose={() => setBurgerOpen(false)} 
        menuItems={menuData.items} 
        preSelectedBurger={preSelectedBurger} 
        availableSides={concept === "lunch" ? lunchMealSides : availableSides}
        conceptId={concept === "lunch" ? "lunch" : "burgers"}
      />

      {/* Wings Flavor Dialog (Krispy/Buffalo/Sweet & Sour) */}
      <Dialog open={!!wingsDialog} onOpenChange={(o) => !o && setWingsDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Select Wing Flavor
            </DialogTitle>
            <DialogDescription>
              {wingsDialog?.name} {wingsDialog?.size} - {wingsDialog && formatPrice(wingsDialog.price)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {wingSauces.map((flavor) => (
              <button
                key={flavor.id}
                data-testid={`wing-flavor-${flavor.id}`}
                className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                style={{
                  borderColor: selectedWingSauce === flavor.name ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                  backgroundColor: selectedWingSauce === flavor.name ? BRAND_COLORS.sunsetOrange + "10" : "white"
                }}
                onClick={() => setSelectedWingSauce(flavor.name)}
              >
                <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{flavor.name}</span>
                {selectedWingSauce === flavor.name && <Check className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />}
              </button>
            ))}
          </div>
          <Button
            data-testid="wings-flavor-next-btn"
            className="w-full rounded-xl h-12 font-bold text-white"
            style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
            disabled={!selectedWingSauce}
            onClick={handleWingsAdd}
          >
            Next: Select Dipping Sauces
          </Button>
        </DialogContent>
      </Dialog>

      {/* Dipping Sauce Selection Dialog (Tiered Free Sauces) - With Quantity Selection */}
      <Dialog open={!!sauceDialog} onOpenChange={(o) => !o && setSauceDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Select Dipping Sauces
            </DialogTitle>
            <DialogDescription>
              {sauceDialog?.item?.name} {sauceDialog?.item?.size} - {sauceDialog?.item && formatPrice(sauceDialog.item.price)}
            </DialogDescription>
          </DialogHeader>
          
          {/* Free sauce info */}
          <div className="px-6 py-2">
            <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "20" }}>
              <p className="text-sm text-center" style={{ color: BRAND_COLORS.deepTeal }}>
                <strong>{sauceDialog?.freeSauces} Free Sauce{sauceDialog?.freeSauces !== 1 ? "s" : ""}</strong>
                {getTotalSauceQty() > (sauceDialog?.freeSauces || 0) && (
                  <span> • Extra: +${getExtraSauceCost().toFixed(2)}</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-4">
            <p className="text-xs mb-2" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              Selected: {getTotalSauceQty()} sauce{getTotalSauceQty() !== 1 ? "s" : ""}
              {getTotalSauceQty() > (sauceDialog?.freeSauces || 0) && ` (${getTotalSauceQty() - (sauceDialog?.freeSauces || 0)} extra @ $0.79 each)`}
            </p>
            <div className="space-y-2">
              {dippingSauces.map((sauce) => {
                const qty = sauceQuantities[sauce.id] || 0;
                return (
                  <div
                    key={sauce.id}
                    data-testid={`sauce-${sauce.id}`}
                    className="flex items-center justify-between p-3 rounded-xl border bg-white"
                    style={{ borderColor: qty > 0 ? BRAND_COLORS.sunsetOrange : "#E5E7EB" }}
                  >
                    <div>
                      <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{sauce.name}</span>
                      {qty > 0 && (
                        <span className="text-xs ml-2" style={{ color: BRAND_COLORS.tropicalAqua }}>
                          {qty <= (sauceDialog?.freeSauces || 0) - (getTotalSauceQty() - qty) ? "Free" : `@ $0.79 each`}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`sauce-minus-${sauce.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                        style={{ backgroundColor: "#E8E8E8" }}
                        onClick={() => updateSauceQty(sauce.id, -1)}
                        disabled={qty === 0}
                      >
                        <Minus className="h-4 w-4" style={{ color: BRAND_COLORS.darkSlate }} />
                      </button>
                      <span className="text-sm font-bold min-w-[24px] text-center" style={{ color: BRAND_COLORS.darkSlate }}>
                        {qty}
                      </span>
                      <button
                        data-testid={`sauce-plus-${sauce.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: BRAND_COLORS.sunsetOrange + "20" }}
                        onClick={() => updateSauceQty(sauce.id, 1)}
                      >
                        <Plus className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-center mt-3" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
              You can skip sauces or add as many as you&apos;d like
            </p>
          </div>

          <div className="px-6 pb-6 pt-2 border-t border-slate-100">
            <Button
              data-testid="sauce-add-btn"
              className="w-full rounded-xl h-12 font-bold text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              onClick={handleSauceAdd}
            >
              {getTotalSauceQty() === 0 
                ? "Skip Sauce - Add to Cart" 
                : `Add to Cart - ${formatPrice((sauceDialog?.item?.price || 0) + getExtraSauceCost())}`
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Side Size Selection Dialog */}
      <Dialog open={!!sideDialog} onOpenChange={(o) => !o && setSideDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Select Size
            </DialogTitle>
            <DialogDescription>{sideDialog?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(sideDialog?.sizes || ["Regular", "Large"]).map((size) => {
              const price = getSidePrice(sideDialog, size);
              return (
                <button
                  key={size}
                  data-testid={`size-${size.toLowerCase()}`}
                  className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                  style={{
                    borderColor: selectedSideSize === size ? BRAND_COLORS.tropicalAqua : "#E5E7EB",
                    backgroundColor: selectedSideSize === size ? BRAND_COLORS.tropicalAqua + "15" : "white"
                  }}
                  onClick={() => setSelectedSideSize(size)}
                >
                  <span className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>{size}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm" style={{ color: BRAND_COLORS.sunsetOrange }}>{formatPrice(price)}</span>
                    {selectedSideSize === size && <Check className="h-4 w-4" style={{ color: BRAND_COLORS.tropicalAqua }} />}
                  </div>
                </button>
              );
            })}
          </div>
          <Button
            data-testid="side-size-add-btn"
            className="w-full rounded-xl h-12 font-bold text-white"
            style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
            disabled={!selectedSideSize}
            onClick={handleSideAdd}
          >
            Add to Cart - {selectedSideSize && formatPrice(getSidePrice(sideDialog, selectedSideSize))}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Meal Dialog */}
      <Dialog open={!!mealDialog} onOpenChange={(o) => !o && setMealDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              {isMealPieces ? "Select Pieces & Biscuit" : "Choose Your Biscuit"}
            </DialogTitle>
            <DialogDescription>{mealDialog?.name} {mealDialog?.size}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {isMealPieces && (
              <div className="mb-4">
                <PieceCounter pieces={mealPieces} total={getMealPieceCount()}
                  onChange={(id, val) => setMealPieces((prev) => ({ ...prev, [id]: val }))} />
              </div>
            )}
            <h4 className="font-bold text-sm mb-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>Biscuit *</h4>
            <div className="space-y-2">
              {BISCUITS.map((b) => (
                <button key={b.id} data-testid={`biscuit-${b.id}`}
                  className="w-full p-3 rounded-xl border-2 text-left font-medium text-sm transition-all"
                  style={{
                    borderColor: selectedBiscuit === b.name ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                    backgroundColor: selectedBiscuit === b.name ? BRAND_COLORS.sunsetOrange + "10" : "white",
                    color: BRAND_COLORS.darkSlate
                  }}
                  onClick={() => setSelectedBiscuit(b.name)}>{b.name}</button>
              ))}
            </div>
          </div>
          <div className="px-6 pb-6 pt-2 border-t border-slate-100">
            <Button data-testid="meal-next-btn" className="w-full rounded-xl h-12 font-bold text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              disabled={!selectedBiscuit || (isMealPieces && mealPieceTotal !== getMealPieceCount())}
              onClick={handleMealAdd}>
              Next: Select Sauces
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Family Meal Dialog */}
      <Dialog open={!!familyDialog} onOpenChange={(o) => !o && setFamilyDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>Select Your Pieces</DialogTitle>
            <DialogDescription>{familyDialog?.name} - {familyDialog && formatPrice(familyDialog.price)}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            <PieceCounter pieces={familyPieces} total={getFamilyPieceCount()}
              onChange={(id, val) => setFamilyPieces((prev) => ({ ...prev, [id]: val }))} />
          </div>
          <div className="px-6 pb-6 pt-2 border-t border-slate-100">
            <Button data-testid="family-next-btn" className="w-full rounded-xl h-12 font-bold text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              disabled={familyPieceTotal !== getFamilyPieceCount()}
              onClick={handleFamilyAdd}>
              Next: Add-ons
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dark Chicken Add-on Dialog (Hot Sauce / Honey only) */}
      <Dialog open={!!darkChickenDialog} onOpenChange={(o) => !o && setDarkChickenDialog(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="dark-chicken-addon-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Optional Add-ons
            </DialogTitle>
            <DialogDescription>
              {darkChickenDialog?.item?.name} {darkChickenDialog?.item?.size} - {darkChickenDialog?.item && formatPrice(darkChickenDialog.item.price)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-3">
            <p className="text-xs text-center mb-4" style={{ color: BRAND_COLORS.tropicalAqua }}>
              These add-ons are <strong>FREE</strong> with your order
            </p>
            
            <div className="space-y-3">
              {/* Hot Sauce Toggle */}
              <button
                data-testid="addon-hot-sauce"
                className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                style={{
                  borderColor: darkChickenAddons.hotSauce ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                  backgroundColor: darkChickenAddons.hotSauce ? BRAND_COLORS.sunsetOrange + "10" : "white"
                }}
                onClick={() => setDarkChickenAddons(prev => ({ ...prev, hotSauce: !prev.hotSauce }))}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🌶️</span>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Hot Sauce</p>
                    <p className="text-xs text-slate-500">Add some heat</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: BRAND_COLORS.tropicalAqua }}>FREE</span>
                  {darkChickenAddons.hotSauce && <Check className="h-5 w-5" style={{ color: BRAND_COLORS.sunsetOrange }} />}
                </div>
              </button>

              {/* Honey Toggle */}
              <button
                data-testid="addon-honey"
                className="w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between"
                style={{
                  borderColor: darkChickenAddons.honey ? BRAND_COLORS.sunsetOrange : "#E5E7EB",
                  backgroundColor: darkChickenAddons.honey ? BRAND_COLORS.sunsetOrange + "10" : "white"
                }}
                onClick={() => setDarkChickenAddons(prev => ({ ...prev, honey: !prev.honey }))}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🍯</span>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: BRAND_COLORS.darkSlate }}>Honey</p>
                    <p className="text-xs text-slate-500">Sweet & delicious</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: BRAND_COLORS.tropicalAqua }}>FREE</span>
                  {darkChickenAddons.honey && <Check className="h-5 w-5" style={{ color: BRAND_COLORS.sunsetOrange }} />}
                </div>
              </button>
            </div>

            <p className="text-xs text-center mt-4" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
              You can skip add-ons or select both
            </p>
          </div>
          
          <Button
            data-testid="dark-chicken-add-btn"
            className="w-full rounded-xl h-12 font-bold text-white"
            style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
            onClick={handleDarkChickenAdd}
          >
            Add to Cart - {darkChickenDialog?.item && formatPrice(darkChickenDialog.item.price)}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
